import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, Star, Smartphone, Search, Zap, Shield, Rocket, MousePointerClick } from "lucide-react";
import { Button } from "../components/ui/button";
import { Container, Reveal, SectionLabel } from "../components/primitives";
import Seo from "../components/Seo";
import Testimonials from "../components/Testimonials";
import api from "../lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1718220216044-006f43e3a9b1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const features = [
  { icon: Smartphone, title: "Mobile-First", desc: "Flawless on every device — phones, tablets, desktops." },
  { icon: Search, title: "SEO Built-In", desc: "Rank higher and get found by local customers." },
  { icon: MousePointerClick, title: "Conversion-Focused", desc: "Designed to turn visitors into paying customers." },
  { icon: Zap, title: "Fast & Reliable", desc: "Lightning-fast load times that keep visitors engaged." },
  { icon: Shield, title: "Secure & Reliable", desc: "SSL, fast hosting and a mobile-first build on every site." },
  { icon: Rocket, title: "72-Hour Delivery", desc: "Launch in days, not months, with our proven process." },
];

const steps = [
  { n: "01", title: "Pick a package", desc: "Choose the plan that fits your business and budget." },
  { n: "02", title: "Complete onboarding", desc: "Tell us about your business — we handle the rest." },
  { n: "03", title: "We design & build", desc: "A premium website crafted around your goals." },
  { n: "04", title: "Review & launch", desc: "Approve your site and go live in 72 hours." },
];

export default function Home() {
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState([]);

  useEffect(() => {
    api.get("/portfolio").then((r) => setPortfolio(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo page="home" />

      {/* Hero */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 grid-bg overflow-hidden">
        <Container className="relative grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Reveal>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#141414] px-4 py-1.5 text-xs font-medium text-[#B7AF98] shadow-sm">
                <span className="flex -space-x-1">
                  {[0, 1, 2].map((i) => <Star key={i} className="h-3.5 w-3.5 fill-[#C9A227] text-[#C9A227]" />)}
                </span>
                Trusted by local businesses nationwide
              </div>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] text-[#F3EDDC]">
                Professional Websites That Help Local Businesses <span className="gold-text">Grow</span>
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-6 text-lg text-[#B7AF98] max-w-xl leading-relaxed">
                Modern, mobile-friendly websites designed to turn visitors into customers.
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button data-testid="hero-primary-cta" size="lg" onClick={() => navigate("/build")} className="gold-shimmer-btn rounded-full bg-[#C9A227] hover:bg-[#B0891C] text-black h-12 px-7 text-base">
                  Build My Website <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button data-testid="hero-secondary-cta" size="lg" variant="outline" onClick={() => navigate("/audit")} className="rounded-full h-12 px-7 text-base border-white/20">
                  Get My Free AI Website Audit
                </Button>
              </div>
            </Reveal>
            <Reveal delay={320}>
              <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#B7AF98]">
                {["From $497", "72-hour delivery", "No hidden fees"].map((t) => (
                  <span key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-[#C9A227]" /> {t}</span>
                ))}
              </div>
            </Reveal>
          </div>
          <Reveal delay={200} className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.15)] border border-white/10">
              <img src={HERO_IMG} alt="Modern web design agency workspace" className="w-full h-[460px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden sm:block rounded-2xl bg-[#141414] shadow-xl border border-white/10 p-5 w-52">
              <div className="text-3xl font-display font-bold text-[#F3EDDC]">+68%</div>
              <div className="text-sm text-[#948C79]">avg. increase in leads after launch</div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Trust bar */}
      <section className="border-y border-white/10 bg-[#0c0c0d]">
        <Container className="py-8 flex flex-wrap items-center justify-center gap-x-12 gap-y-4 text-[#7E7663] text-sm font-medium uppercase tracking-widest">
          {["Roofing", "Restaurants", "Landscaping", "Real Estate", "Med Spas"].map((c) => (
            <span key={c}>{c}</span>
          ))}
        </Container>
      </section>

      {/* Features */}
      <section className="py-24">
        <Container>
          <div className="max-w-2xl">
            <SectionLabel>Why WebLaunch</SectionLabel>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#F3EDDC]">
              Everything your business needs to win online
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <Reveal key={f.title} delay={i * 60}>
                <div className="group rounded-2xl border border-white/10 bg-[#141414] p-7 shadow-[0_8px_32px_rgba(0,0,0,0.04)] hover:-translate-y-1 transition-transform duration-300">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#C9A227]/10 text-[#C9A227]">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-semibold text-[#F3EDDC]">{f.title}</h3>
                  <p className="mt-2 text-[#B7AF98] leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Process */}
      <section className="py-24 bg-black text-white">
        <Container>
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#E4C96A]">How it works</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">
              From idea to launched in four steps
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-8">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 60}>
                <div className="relative">
                  <div className="font-display text-5xl font-bold text-white/10">{s.n}</div>
                  <h3 className="mt-2 font-display text-xl font-semibold">{s.title}</h3>
                  <p className="mt-2 text-[#7E7663] leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Portfolio preview */}
      <section className="py-24">
        <Container>
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-2xl">
              <SectionLabel>Selected Work</SectionLabel>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#F3EDDC]">
                Concept projects we're proud of
              </h2>
              <p className="mt-3 text-[#948C79] text-sm">Fictional concept projects that demonstrate what we can build for you.</p>
            </div>
            <Button variant="ghost" onClick={() => navigate("/portfolio")} className="text-[#C9A227]">
              View all <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {portfolio.map((p, i) => (
              <Reveal key={p.slug} delay={i * 70}>
                <Link to={`/portfolio/${p.slug}`} data-testid={`home-portfolio-${p.slug}`} className="group block rounded-2xl overflow-hidden border border-white/10 bg-[#141414] hover:-translate-y-1 transition-transform duration-300">
                  <div className="relative h-56 overflow-hidden">
                    <img src={p.hero_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute top-3 left-3 rounded-full bg-[#141414]/90 backdrop-blur px-3 py-1 text-xs font-semibold text-[#D2CAB2]">Concept</span>
                  </div>
                  <div className="p-5">
                    <div className="text-xs uppercase tracking-widest text-[#7E7663]">{p.industry}</div>
                    <h3 className="mt-1 font-display text-lg font-semibold text-[#F3EDDC]">{p.title}</h3>
                    <p className="mt-1 text-sm text-[#948C79]">{p.tagline}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <Testimonials />

      {/* CTA */}
      <section className="pb-24">
        <Container>
          <div className="relative overflow-hidden rounded-3xl bg-black border border-[#C9A227]/30 px-8 py-16 sm:px-16 sm:py-20 text-center">
            <div className="absolute inset-0 grid-bg opacity-30" />
            <div className="relative">
              <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C9A227]">WebLaunch Studio</span>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">Ready to grow your business?</h2>
              <p className="mt-4 text-white/70 max-w-xl mx-auto">Get a professional website designed to bring you more customers. Starting at $497.</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Button data-testid="home-cta-get" size="lg" onClick={() => navigate("/pricing")} className="gold-shimmer-btn rounded-full bg-[#C9A227] text-black hover:bg-[#E4C96A] h-12 px-7 text-base font-semibold">
                  Get Your Website
                </Button>
                <Button data-testid="home-cta-contact" size="lg" variant="outline" onClick={() => navigate("/contact")} className="rounded-full h-12 px-7 text-base border-[#C9A227]/40 bg-transparent text-white hover:bg-[#C9A227]/10">
                  Talk to us
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
